<template>
  <v-flex :xs6="userCanAddObjects" style="padding: 0 20px">
    <div
      v-if="actionType !== 'edit' && actionType !== 'view'"
      style="height: 20px"
    />

    <home-form-table-toolbar class="mt-2">
      <home-form-table-select-all-tool
        :disabled="!toolbarEnabled"
        @click="selectAll"
      />
      <home-form-table-select-invert-tool
        :disabled="!toolbarEnabled"
        @click="invertSelection"
      />
      <home-form-table-select-none-tool
        :disabled="!toolbarEnabled"
        @click="clearSelection"
      />

      <div style="width: 100%" />
      <home-form-table-remove-from-list-tool
        :disabled="!toolbarEnabled"
        :tooltip="$t('RemoveFromList')"
        @click="removeAssetsFromReservation"
      />
    </home-form-table-toolbar>

    <div
      class="multi-selection-table-container no-select scroll-fire-fox"
      style="height: fit-content; overflow: scroll; max-height: 90%;"
    >
      <table
        class="multi-selection-table"
        style="position: relative"
        id="table"
      >
        <tr class="table-header">
          <th class="object-index" />
          <th>{{ $tc("Layer", 1) }}</th>
          <th>ID</th>
          <th>{{ $tc("Label", 1) }}</th>
          <th>{{ $t("DroppedDate") }}</th>
        </tr>

        <tr
          class="table-body"
          v-for="(object, objectIndex) in assetReservationObjects"
          :key="objectIndex"
          :class="{
            selected: selectedIndexes.includes(objectIndex)
          }"
          :style="{ pointerEvents: actionType === 'view' ? 'none' : 'auto' }"
          @click="select(objectIndex, $event)"
        >
          <td class="object-index">{{ objectIndex + 1 }}</td>

          <td class="input-disabled">{{ getObjectLayer(object) }}</td>

          <td class="input-disabled">
            {{ getAssetReservationObjectID(object) }}
          </td>

          <td class="input-disabled">
            {{ getAssetReservationObjectLabel(object) }}
          </td>

          <td class="input-disabled">
            {{ getAssetReservationObjectDroppedDate(object) }}
          </td>
        </tr>
      </table>

      <div
        v-if="assetReservationObjects.length === 0"
        style="font-size: 14px; text-align: center; margin-top: 5%"
      >
        {{ $t('NoAssetsWarning') }}
      </div>
    </div>

    <app-dialog-box
      :open="dissociateObjectDialog.open"
      :title="$t('Delete')"
      :content="
        $t('DissociateAssetReservationObject') +
          '\n\n' +
          $t('ContinueConfirmation')
      "
      :main-action-label="$t('No')"
      @mainAction="closeDissociateObjectDialog"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="dissociateObject"
    />
  </v-flex>
</template>

<script>
import { bus } from "@/main";
import { multipleObjectsTableMixin } from "@/mixins/forms/multipleObjectsSelection/multipleObjectsTableMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import MultipleObjectsMultipleLayersTable from "@/components/Home/Forms/_Organisms/MultipleObjectsMultipleLayersTable";
import HomeFormTableToolbar from "@/components/Home/Forms/_Atoms/TableToolbar";
import HomeFormTableRefreshTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/Refresh";
import HomeFormTableSelectAllTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/SelectAll";
import HomeFormTableSelectInvertTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/SelectInvert";
import HomeFormTableSelectNoneTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/SelectNone";
import HomeFormTableRemoveFromListTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/RemoveFromList";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import HomeFormTableToolbarButton from "@/components/Home/Forms/_Atoms/TableToolbarButton";

export default {
  name: "AssetReservationFormSelectedAssetsSection",

  components: {
    HomeFormTableToolbarButton,
    AppDialogBox,
    HomeFormTableRemoveFromListTool,
    HomeFormTableSelectNoneTool,
    HomeFormTableSelectInvertTool,
    HomeFormTableSelectAllTool,
    HomeFormTableRefreshTool,
    HomeFormTableToolbar,
    MultipleObjectsMultipleLayersTable
  },

  mixins: [multipleObjectsTableMixin, checkPermissionsMixin],

  props: {
    actionType: {
      type: String
    },

    assetReservationObjects: {
      type: Array
    },

    tab: {
      type: String
    },

    userCanAddObjects: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      dissociateObjectDialog: {
        open: false,
        object: null
      }
    };
  },

  computed: {
    toolbarEnabled() {
      return this.actionType !== "view" && this.objects.length > 0;
    },

    hasPermissionToRemoveAssetReservationObjects() {
      return this.userHasFibercloudPermission(
        "asset_reservation_object",
        "destroy"
      );
    }
  },

  watch: {
    assetReservationObjects() {
      this.updateObjects();
    }
  },

  mounted() {
    bus.$on("add-assets-to-asset-reservation", selectedObjects =>
      this.addAssets(selectedObjects)
    );
    this.updateObjects();
  },

  destroyed() {
    bus.$off("add-assets-to-asset-reservation");
  },

  methods: {
    getObjectLayer(object) {
      let layerTranslationKey = this.$store.getters.getLayerTranslationKey(
        object.aro_object_layer
      );

      return this.$i18n.tc(layerTranslationKey, 1);
    },

    getLayerPrefix(layer) {
      return this.$store.getters.getLayerInitials(layer);
    },

    getAssetReservationObjectID(object) {
      let prefix = this.getLayerPrefix(object.aro_object_layer);
      let id = object.asset[prefix + "_id"];
      if (!id) {
        id = "-";
      }
      return id;
    },

    getAssetReservationObjectLabel(object) {
      let prefix = this.getLayerPrefix(object.aro_object_layer);
      let label = object.asset[prefix + "_label"];
      if (!label) {
        label = "-";
      }
      return label;
    },

    getAssetReservationObjectDroppedDate(object) {
      let droppedDate = object.aro_dropped_date;
      if (!droppedDate) {
        droppedDate = "-";
      }
      return droppedDate;
    },

    addAssets(selectedObjects) {
      let assetReservationObjects = this.assetReservationObjects;

      let layer = selectedObjects.layer;
      for (const object of selectedObjects.objects) {
        let objectUUID = this.getObjectUUID(layer, object);
        let assetReservationObjectIndex = this.getAssetReservationObjectIndex(
          layer,
          objectUUID
        );
        if (assetReservationObjectIndex >= 0) {
          let assetReservationObject =
            assetReservationObjects[assetReservationObjectIndex];
          if (assetReservationObject.aro_id && assetReservationObject.delete) {
            delete assetReservationObject.delete;
          }
        } else {
          assetReservationObjects.push({
            aro_object_layer: layer,
            aro_object_uuid: objectUUID,
            asset: object
          });
        }
      }
      this.updateAssetReservationObjects(assetReservationObjects);
    },

    getObjectUUID(layer, object) {
      let prefix = this.getLayerPrefix(layer);
      return object[prefix + "_uuid"];
    },

    getAssetReservationObjectIndex(layer, uuid) {
      if (
        this.assetReservationObjects &&
        this.assetReservationObjects.length > 0
      ) {
        return this.assetReservationObjects.findIndex(
          item =>
            item.aro_object_layer === layer && item.aro_object_uuid === uuid
        );
      }
      return -1;
    },

    updateAssetReservationObjects(objects) {
      this.$emit("update-asset-reservation-objects", objects);
      this.updateObjects();
    },

    updateObjects() {
      this.$nextTick(() => {
        this.objects = this.assetReservationObjects.filter(
          item => !item.hasOwnProperty("delete")
        );
      });
    },

    tryDissociateObject(object) {
      if (object.aro_id) {
        this.dissociateObjectDialog.object = object;
        this.dissociateObjectDialog.open = true;
      }
    },

    closeDissociateObjectDialog() {
      this.dissociateObjectDialog.object = null;
      this.dissociateObjectDialog.open = false;
    },

    dissociateObject() {
      this.$emit('add-asset-to-delete', this.dissociateObjectDialog.object);
      this.closeDissociateObjectDialog();
    },

    removeAssetsFromReservation() {
      let assetReservationObjects = [...this.assetReservationObjects];

      this.selectedIndexes.sort((a, b) => { return b - a })
      for (let i = 0; i < this.selectedIndexes.length; i++){
        assetReservationObjects.splice(this.selectedIndexes[i], 1);
      }

      this.updateAssetReservationObjects(assetReservationObjects);
      this.clearSelection();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/AssetReservationForm/AssetsTab/SelectedAssetReservationObjects.vue